import { motion } from 'framer-motion';
import { Check, Trophy } from 'lucide-react';
import { V1_STATS, V2_STATS, COPY } from '../config/orbConfig';

const BetterBadge = ({ show }) => {
  if (!show) return null;
  return (
    <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium text-[#10B981] bg-[#10B981]/10 px-1.5 py-0.5 rounded">
      <Trophy size={10} /> Better
    </span>
  );
};

export const ComparisonTable = () => {
  const rows = [
    {
      metric: 'Total Return (1yr)',
      v1: `+${V1_STATS.oneYear.totalReturn}%`,
      v2: `+${V2_STATS.oneYear.totalReturn}%`,
      v1Better: V1_STATS.oneYear.totalReturn > V2_STATS.oneYear.totalReturn,
      v2Better: V2_STATS.oneYear.totalReturn > V1_STATS.oneYear.totalReturn,
    },
    {
      metric: 'Win Rate',
      v1: `${V1_STATS.oneYear.winRate}%`,
      v2: `${V2_STATS.oneYear.winRate}%`,
      v1Better: V1_STATS.oneYear.winRate > V2_STATS.oneYear.winRate,
      v2Better: V2_STATS.oneYear.winRate > V1_STATS.oneYear.winRate,
    },
    {
      metric: 'Max Drawdown',
      v1: `${V1_STATS.oneYear.maxDrawdown}%`,
      v2: `${V2_STATS.oneYear.maxDrawdown}%`,
      v1Better: V1_STATS.oneYear.maxDrawdown < V2_STATS.oneYear.maxDrawdown,
      v2Better: V2_STATS.oneYear.maxDrawdown < V1_STATS.oneYear.maxDrawdown,
      note: 'Lower is better',
    },
    {
      metric: 'Profit Factor',
      v1: V1_STATS.oneYear.profitFactor.toString(),
      v2: V2_STATS.oneYear.profitFactor.toString(),
      v1Better: V1_STATS.oneYear.profitFactor > V2_STATS.oneYear.profitFactor,
      v2Better: V2_STATS.oneYear.profitFactor > V1_STATS.oneYear.profitFactor,
    },
    {
      metric: 'Total Trades (1yr)',
      v1: V1_STATS.oneYear.totalTrades.toString(),
      v2: V2_STATS.oneYear.totalTrades.toString(),
      v1Better: false,
      v2Better: V2_STATS.oneYear.totalTrades > V1_STATS.oneYear.totalTrades,
      note: 'More = more opportunities',
    },
    {
      metric: 'Trading Style',
      v1: 'Fewer, pickier trades',
      v2: 'Twice the trades',
      v1Better: false,
      v2Better: false,
    },
    {
      metric: 'Best For',
      v1: COPY.comparison.v1BestFor,
      v2: COPY.comparison.v2BestFor,
      v1Better: false,
      v2Better: false,
    },
    {
      metric: 'Recommended Risk',
      v1: `${V1_STATS.recommendedRisk}%`,
      v2: `${V2_STATS.recommendedRisk}%`,
      v1Better: false,
      v2Better: false,
      note: 'For 4% DD limit',
    },
    {
      metric: 'Platforms',
      v1: 'MT5 + NT8',
      v2: 'MT5 + NT8',
      v1Better: false,
      v2Better: false,
    },
    {
      metric: 'Included in Plan',
      v1: <Check size={18} className="text-[#10B981]" />,
      v2: <Check size={18} className="text-[#10B981]" />,
      v1Better: false,
      v2Better: false,
    },
  ];

  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-3">
            Head-to-Head Comparison
          </h2>
          <p className="text-white/50 text-sm">
            Every metric, side by side
          </p>
        </div>
        
        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl border border-white/10 overflow-hidden"
        >
          {/* Header Row */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-white/5 border-b border-white/10">
            <div className="text-white/60 text-sm font-medium">Metric</div>
            <div className="text-center">
              <span className="font-heading font-bold text-white">V1</span>
              <span className="text-[#8B5CF6] ml-2 text-sm">Conservative</span>
            </div>
            <div className="text-center">
              <span className="font-heading font-bold text-white">V2</span>
              <span className="text-[#06B6D4] ml-2 text-sm">Growth</span>
            </div>
          </div>
          
          {/* Data Rows */}
          {rows.map((row, idx) => (
            <div 
              key={row.metric}
              className={`grid grid-cols-3 gap-4 p-4 ${idx !== rows.length - 1 ? 'border-b border-white/5' : ''}`}
            >
              <div>
                <div className="text-white/80 text-sm">{row.metric}</div>
                {row.note && (
                  <div className="text-white/30 text-xs mt-0.5">{row.note}</div>
                )}
              </div>
              <div className="text-center">
                <span className={`text-sm font-medium ${row.v1Better ? 'text-white' : 'text-white/70'}`}>
                  {row.v1}
                </span>
                <BetterBadge show={row.v1Better} />
              </div>
              <div className="text-center">
                <span className={`text-sm font-medium ${row.v2Better ? 'text-white' : 'text-white/70'}`}>
                  {row.v2}
                </span>
                <BetterBadge show={row.v2Better} />
              </div>
            </div>
          ))}
        </motion.div>
        
        {/* Note */}
        <p className="text-center text-white/30 text-xs mt-4">
          All data from 1-year backtests ({V1_STATS.oneYear.dateRange}). Past performance does not guarantee future results.
        </p>
      </div>
    </section>
  );
};

export default ComparisonTable;
